'use client';

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useChat } from '@/lib/hooks/useChat';
import { Brain, Zap, Target, Shield, TrendingUp, Play, RotateCcw, ChevronRight, Star, Award, Clock, Activity, Dumbbell, Sparkles, AlertCircle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSession } from './SessionProvider';

// ─── Types ────────────────────────────────────────────────────────────────────

type Domain = 'attention' | 'memory' | 'processing' | 'executive' | 'language';
type Difficulty = 'neuroprotect' | 'rehabilitate' | 'enhance';
type ExercisePhase = 'idle' | 'briefing' | 'active' | 'result' | 'ai_insight';

interface Exercise {
  id: string;
  domain: Domain;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  xpReward: number;
  durationSec: number;
}

interface SessionResult {
  exerciseId: string;
  score: number;
  accuracy: number;
  reactionMs: number;
  xpEarned: number;
  domain: Domain;
}

interface DomainProgress {
  domain: Domain;
  score: number;
  sessions: number;
  trend: number;
}

// ─── Exercise Definitions ─────────────────────────────────────────────────────

const EXERCISES: Exercise[] = [
  {
    id: 'n-back',
    domain: 'memory',
    title: 'Neural N-Back',
    description: 'Working memory training — identify when a stimulus matches one shown N steps earlier. Proven to improve fluid intelligence and hippocampal plasticity.',
    icon: <Brain size={20} />,
    color: '#8B5CF6',
    xpReward: 120,
    durationSec: 45,
  },
  {
    id: 'attention-spotlight',
    domain: 'attention',
    title: 'Attention Spotlight',
    description: 'Selective attention training — track a target among distractors. Strengthens prefrontal-parietal attention networks and reduces attentional blink.',
    icon: <Target size={20} />,
    color: '#F97316',
    xpReward: 100,
    durationSec: 40,
  },
  {
    id: 'processing-speed',
    domain: 'processing',
    title: 'Synapse Sprint',
    description: 'Rapid stimulus-response training — maximise processing speed and neural conduction velocity. Targets white matter integrity and reaction time.',
    icon: <Zap size={20} />,
    color: '#06B6D4',
    xpReward: 90,
    durationSec: 30,
  },
  {
    id: 'executive-switch',
    domain: 'executive',
    title: 'Cortex Switch',
    description: 'Task-switching and cognitive flexibility — alternate between rules rapidly. Trains dorsolateral prefrontal cortex and reduces perseveration.',
    icon: <Activity size={20} />,
    color: '#10B981',
    xpReward: 130,
    durationSec: 50,
  },
  {
    id: 'pattern-shield',
    domain: 'memory',
    title: 'Pattern Shield',
    description: 'Visuospatial pattern recognition and recall — reconstruct sequences from memory. Targets posterior cortical networks and spatial working memory.',
    icon: <Shield size={20} />,
    color: '#EF4444',
    xpReward: 110,
    durationSec: 45,
  },
];

const DOMAIN_LABELS: Record<Domain, string> = {
  attention: 'Attention',
  memory: 'Memory',
  processing: 'Processing Speed',
  executive: 'Executive Function',
  language: 'Language',
};

const DIFFICULTY_CONFIG: Record<Difficulty, { label: string; color: string; description: string }> = {
  neuroprotect: { label: 'Neuroprotect', color: '#10B981', description: 'Gentle stimulation to maintain and protect existing neural pathways' },
  rehabilitate: { label: 'Rehabilitate', color: '#F97316', description: 'Targeted recovery exercises for damaged or weakened cognitive circuits' },
  enhance: { label: 'Enhance', color: '#8B5CF6', description: 'High-intensity neuroplasticity training to push cognitive limits' },
};

// ─── N-Back Exercise ──────────────────────────────────────────────────────────

function NBackExercise({ difficulty, onComplete }: { difficulty: Difficulty; onComplete: (score: number, accuracy: number, reactionMs: number) => void }) {
  const n = difficulty === 'neuroprotect' ? 1 : difficulty === 'rehabilitate' ? 2 : 3;
  const GRID_SIZE = 3;
  const TOTAL_TRIALS = difficulty === 'neuroprotect' ? 12 : difficulty === 'rehabilitate' ? 16 : 20;
  const INTERVAL_MS = difficulty === 'neuroprotect' ? 2500 : difficulty === 'rehabilitate' ? 2000 : 1600;

  const [sequence, setSequence] = useState<number[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [activeCell, setActiveCell] = useState<number | null>(null);
  const [userResponses, setUserResponses] = useState<boolean[]>([]);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [reactionTimes, setReactionTimes] = useState<number[]>([]);
  const [stimulusTime, setStimulusTime] = useState(0);
  const [done, setDone] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const seq = Array.from({ length: TOTAL_TRIALS }, () => Math.floor(Math.random() * GRID_SIZE * GRID_SIZE));
    setSequence(seq);
    let idx = 0;
    const tick = () => {
      if (idx >= TOTAL_TRIALS) {
        setDone(true);
        setActiveCell(null);
        return;
      }
      setCurrentIdx(idx);
      setActiveCell(seq[idx]);
      setStimulusTime(Date.now());
      idx++;
    };
    tick();
    intervalRef.current = setInterval(tick, INTERVAL_MS);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  useEffect(() => {
    if (done && sequence.length > 0) {
      let correct = 0;
      let total = 0;
      for (let i = n; i < sequence.length; i++) {
        const isMatch = sequence[i] === sequence[i - n];
        const responded = userResponses[i] === true;
        if (isMatch) { total++; if (responded) correct++; }
        else if (responded) { correct = Math.max(0, correct - 1); }
      }
      const accuracy = total > 0 ? Math.round((correct / total) * 100) : 50;
      const avgReaction = reactionTimes.length > 0 ? Math.round(reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length) : 800;
      const score = Math.round(accuracy * 0.7 + Math.max(0, (2000 - avgReaction) / 20) * 0.3);
      onComplete(Math.min(100, score), accuracy, avgReaction);
    }
  }, [done]);

  const handleMatch = () => {
    if (currentIdx < n || done) return;
    const rt = Date.now() - stimulusTime;
    setReactionTimes(prev => [...prev, rt]);
    const isActualMatch = sequence[currentIdx] === sequence[currentIdx - n];
    setFeedback(isActualMatch ? 'correct' : 'wrong');
    setUserResponses(prev => { const next = [...prev]; next[currentIdx] = true; return next; });
    setTimeout(() => setFeedback(null), 500);
  };

  return (
    <div className="flex flex-col items-center gap-5">
      <div className="text-center">
        <p className="text-[#8BAFD4] text-[12px] mb-1">N-Back Level: <span className="text-white font-bold">{n}</span></p>
        <p className="text-[#4A7AB5] text-[11px]">Does the current position match {n} step{n > 1 ? 's' : ''} ago?</p>
      </div>

      {/* Grid */}
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`, width: 'min(180px, 60vw)' }}
      >
        {Array.from({ length: GRID_SIZE * GRID_SIZE }, (_, i) => (
          <div
            key={i}
            className="rounded-lg transition-all duration-150"
            style={{
              width: 'min(52px, 18vw)',
              height: 'min(52px, 18vw)',
              background: activeCell === i
                ? feedback === 'correct' ? 'rgba(16,185,129,0.8)' : feedback === 'wrong' ? 'rgba(239,68,68,0.8)' : 'rgba(139,92,246,0.8)'
                : 'rgba(30,58,95,0.3)',
              border: activeCell === i ? '2px solid rgba(139,92,246,0.9)' : '1px solid rgba(30,58,95,0.5)',
              boxShadow: activeCell === i ? '0 0 16px rgba(139,92,246,0.5)' : 'none',
            }}
          />
        ))}
      </div>

      {/* Progress */}
      <div className="w-full max-w-[200px]">
        <div className="flex justify-between text-[10px] text-[#4A7AB5] mb-1">
          <span>Trial {Math.min(currentIdx + 1, TOTAL_TRIALS)}</span>
          <span>{TOTAL_TRIALS} total</span>
        </div>
        <div className="h-1 bg-[#1E3A5F]/40 rounded-full overflow-hidden">
          <div className="h-full bg-[#8B5CF6] rounded-full transition-all" style={{ width: `${((currentIdx + 1) / TOTAL_TRIALS) * 100}%` }} />
        </div>
      </div>

      <button
        onClick={handleMatch}
        disabled={currentIdx < n || done}
        className="px-8 py-3 rounded-xl font-bold text-[14px] text-white transition-all active:scale-95"
        style={{
          background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
          boxShadow: '0 4px 16px rgba(139,92,246,0.4)',
          opacity: currentIdx < n || done ? 0.4 : 1,
          minHeight: '48px',
        }}
      >
        ✓ Match!
      </button>
    </div>
  );
}

// ─── Attention Spotlight Exercise ─────────────────────────────────────────────

function AttentionSpotlightExercise({ difficulty, onComplete }: { difficulty: Difficulty; onComplete: (score: number, accuracy: number, reactionMs: number) => void }) {
  const TOTAL = difficulty === 'neuroprotect' ? 10 : difficulty === 'rehabilitate' ? 15 : 20;
  const DISTRACTORS = difficulty === 'neuroprotect' ? 3 : difficulty === 'rehabilitate' ? 5 : 8;
  const DISPLAY_MS = difficulty === 'neuroprotect' ? 1800 : difficulty === 'rehabilitate' ? 1400 : 1000;

  const [trial, setTrial] = useState(0);
  const [items, setItems] = useState<{ x: number; y: number; isTarget: boolean }[]>([]);
  const [showItems, setShowItems] = useState(false);
  const [results, setResults] = useState<{ correct: boolean; rt: number }[]>([]);
  const [stimTime, setStimTime] = useState(0);
  const [done, setDone] = useState(false);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);

  const generateTrial = useCallback(() => {
    const positions: { x: number; y: number; isTarget: boolean }[] = [];
    const targetX = 10 + Math.random() * 80;
    const targetY = 10 + Math.random() * 80;
    positions.push({ x: targetX, y: targetY, isTarget: true });
    for (let i = 0; i < DISTRACTORS; i++) {
      positions.push({ x: 5 + Math.random() * 90, y: 5 + Math.random() * 90, isTarget: false });
    }
    setItems(positions);
    setShowItems(true);
    setStimTime(Date.now());
    setTimeout(() => setShowItems(false), DISPLAY_MS);
  }, [DISTRACTORS, DISPLAY_MS]);

  useEffect(() => {
    if (trial < TOTAL) {
      const t = setTimeout(generateTrial, 600);
      return () => clearTimeout(t);
    } else {
      setDone(true);
    }
  }, [trial]);

  useEffect(() => {
    if (done && results.length > 0) {
      let correct = results.filter(r => r.correct).length;
      const accuracy = Math.round((correct / results.length) * 100);
      const avgRt = Math.round(results.reduce((a, b) => a + b.rt, 0) / results.length);
      const score = Math.round(accuracy * 0.6 + Math.max(0, (1500 - avgRt) / 15) * 0.4);
      onComplete(Math.min(100, score), accuracy, avgRt);
    }
  }, [done]);

  const handleClick = (isTarget: boolean) => {
    if (!showItems) return;
    const rt = Date.now() - stimTime;
    setFeedback(isTarget ? 'correct' : 'wrong');
    setResults(prev => [...prev, { correct: isTarget, rt }]);
    setShowItems(false);
    setTimeout(() => { setFeedback(null); setTrial(t => t + 1); }, 400);
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <p className="text-[#8BAFD4] text-[12px] text-center">Click the <span className="text-[#F97316] font-bold">orange circle</span> — ignore the blue distractors</p>

      <div
        className="relative rounded-xl overflow-hidden w-full"
        style={{ maxWidth: '280px', height: 'min(200px, 55vw)', background: 'rgba(6,13,26,0.8)', border: '1px solid rgba(30,58,95,0.5)' }}
      >
        {showItems && items.map((item, i) => (
          <button
            key={i}
            onClick={() => handleClick(item.isTarget)}
            className="absolute rounded-full transition-all active:scale-90"
            style={{
              left: `${item.x}%`,
              top: `${item.y}%`,
              transform: 'translate(-50%, -50%)',
              width: item.isTarget ? '28px' : '22px',
              height: item.isTarget ? '28px' : '22px',
              background: item.isTarget ? '#F97316' : '#2D5A8E',
              boxShadow: item.isTarget ? '0 0 12px rgba(249,115,22,0.6)' : 'none',
              border: item.isTarget ? '2px solid rgba(249,115,22,0.8)' : '1px solid rgba(45,90,142,0.5)',
              minHeight: '28px',
            }}
          />
        ))}
        {!showItems && !done && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2 h-2 rounded-full bg-[#1E3A5F] animate-pulse" />
          </div>
        )}
        {feedback && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-[28px]">{feedback === 'correct' ? '✓' : '✗'}</span>
          </div>
        )}
      </div>

      <div className="w-full max-w-[280px]">
        <div className="flex justify-between text-[10px] text-[#4A7AB5] mb-1">
          <span>Trial {Math.min(trial + 1, TOTAL)}</span>
          <span>{results.filter(r => r.correct).length}/{results.length} correct</span>
        </div>
        <div className="h-1 bg-[#1E3A5F]/40 rounded-full overflow-hidden">
          <div className="h-full bg-[#F97316] rounded-full transition-all" style={{ width: `${(trial / TOTAL) * 100}%` }} />
        </div>
      </div>
    </div>
  );
}

// ─── Processing Speed Exercise ────────────────────────────────────────────────

function SynapseSprintExercise({ difficulty, onComplete }: { difficulty: Difficulty; onComplete: (score: number, accuracy: number, reactionMs: number) => void }) {
  const TOTAL = 20;
  const [trial, setTrial] = useState(0);
  const [stimulus, setStimulus] = useState<{ symbol: string; isTarget: boolean } | null>(null);
  const [target, setTarget] = useState('');
  const [results, setResults] = useState<{ correct: boolean; rt: number }[]>([]);
  const [stimTime, setStimTime] = useState(0);
  const [done, setDone] = useState(false);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const SYMBOLS = ['◆', '●', '▲', '■', '★', '◉'];

  useEffect(() => {
    const t = SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
    setTarget(t);
  }, []);

  const showNext = useCallback(() => {
    if (trial >= TOTAL) { setDone(true); return; }
    const isTarget = Math.random() > 0.5;
    const sym = isTarget ? target : SYMBOLS.filter(s => s !== target)[Math.floor(Math.random() * (SYMBOLS.length - 1))];
    setStimulus({ symbol: sym, isTarget });
    setStimTime(Date.now());
  }, [trial, target]);

  useEffect(() => {
    if (target) {
      const t = setTimeout(showNext, 500);
      return () => clearTimeout(t);
    }
  }, [trial, target]);

  useEffect(() => {
    if (done && results.length > 0) {
      let correct = results.filter(r => r.correct).length;
      const accuracy = Math.round((correct / results.length) * 100);
      const avgRt = Math.round(results.reduce((a, b) => a + b.rt, 0) / results.length);
      const score = Math.round(accuracy * 0.5 + Math.max(0, (1000 - avgRt) / 10) * 0.5);
      onComplete(Math.min(100, score), accuracy, avgRt);
    }
  }, [done]);

  const respond = (pressed: boolean) => {
    if (!stimulus || done) return;
    const rt = Date.now() - stimTime;
    let correct = pressed === stimulus.isTarget;
    setFeedback(correct ? 'correct' : 'wrong');
    setResults(prev => [...prev, { correct, rt }]);
    setStimulus(null);
    setTimeout(() => { setFeedback(null); setTrial(t => t + 1); }, 300);
  };

  return (
    <div className="flex flex-col items-center gap-5">
      <p className="text-[#8BAFD4] text-[12px] text-center">
        Press <span className="text-[#06B6D4] font-bold">YES</span> when you see: <span className="text-white text-[20px] font-bold">{target}</span>
      </p>

      <div
        className="flex items-center justify-center rounded-2xl"
        style={{ width: '120px', height: '120px', background: 'rgba(6,13,26,0.8)', border: '1px solid rgba(30,58,95,0.5)' }}
      >
        {stimulus ? (
          <span
            className="text-[48px] transition-all"
            style={{ color: feedback === 'correct' ? '#10B981' : feedback === 'wrong' ? '#EF4444' : '#06B6D4' }}
          >
            {stimulus.symbol}
          </span>
        ) : (
          <div className="w-2 h-2 rounded-full bg-[#1E3A5F] animate-pulse" />
        )}
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => respond(true)}
          className="px-6 py-2.5 rounded-xl font-bold text-[13px] text-white transition-all active:scale-95"
          style={{ background: 'linear-gradient(135deg, #06B6D4, #0891B2)', boxShadow: '0 4px 12px rgba(6,182,212,0.3)' }}
        >
          YES ✓
        </button>
        <button
          onClick={() => respond(false)}
          className="px-6 py-2.5 rounded-xl font-bold text-[13px] text-white transition-all active:scale-95"
          style={{ background: 'linear-gradient(135deg, #374151, #1F2937)', border: '1px solid rgba(30,58,95,0.5)' }}
        >
          NO ✗
        </button>
      </div>

      <div className="w-full max-w-[200px]">
        <div className="flex justify-between text-[10px] text-[#4A7AB5] mb-1">
          <span>Trial {Math.min(trial + 1, TOTAL)}</span>
          <span>{results.filter(r => r.correct).length}/{results.length} correct</span>
        </div>
        <div className="h-1 bg-[#1E3A5F]/40 rounded-full overflow-hidden">
          <div className="h-full bg-[#06B6D4] rounded-full transition-all" style={{ width: `${(trial / TOTAL) * 100}%` }} />
        </div>
      </div>
    </div>
  );
}

// ─── Executive Switch Exercise ────────────────────────────────────────────────

function CortexSwitchExercise({ difficulty, onComplete }: { difficulty: Difficulty; onComplete: (score: number, accuracy: number, reactionMs: number) => void }) {
  const TOTAL = difficulty === 'neuroprotect' ? 12 : difficulty === 'rehabilitate' ? 18 : 24;
  const RULES = ['Is it > 5?', 'Is it even?', 'Is it < 5?'];
  const [trial, setTrial] = useState(0);
  const [ruleIdx, setRuleIdx] = useState(0);
  const [number, setNumber] = useState(0);
  const [results, setResults] = useState<{ correct: boolean; rt: number }[]>([]);
  const [stimTime, setStimTime] = useState(0);
  const [done, setDone] = useState(false);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);

  const showNext = useCallback(() => {
    if (trial >= TOTAL) { setDone(true); return; }
    const newRule = trial % 3 === 0 ? (ruleIdx + 1) % RULES.length : ruleIdx;
    setRuleIdx(newRule);
    setNumber(1 + Math.floor(Math.random() * 9));
    setStimTime(Date.now());
  }, [trial, ruleIdx]);

  useEffect(() => {
    const t = setTimeout(showNext, 400);
    return () => clearTimeout(t);
  }, [trial]);

  useEffect(() => {
    if (done && results.length > 0) {
      let correct = results.filter(r => r.correct).length;
      const accuracy = Math.round((correct / results.length) * 100);
      const avgRt = Math.round(results.reduce((a, b) => a + b.rt, 0) / results.length);
      const score = Math.round(accuracy * 0.65 + Math.max(0, (1200 - avgRt) / 12) * 0.35);
      onComplete(Math.min(100, score), accuracy, avgRt);
    }
  }, [done]);

  const getCorrectAnswer = () => {
    if (ruleIdx === 0) return number > 5;
    if (ruleIdx === 1) return number % 2 === 0;
    return number < 5;
  };

  const respond = (answer: boolean) => {
    if (done) return;
    const rt = Date.now() - stimTime;
    let correct = answer === getCorrectAnswer();
    setFeedback(correct ? 'correct' : 'wrong');
    setResults(prev => [...prev, { correct, rt }]);
    setTimeout(() => { setFeedback(null); setTrial(t => t + 1); }, 350);
  };

  return (
    <div className="flex flex-col items-center gap-5">
      <div
        className="px-4 py-2 rounded-lg text-[12px] font-bold"
        style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', color: '#10B981' }}
      >
        Rule: {RULES[ruleIdx]}
      </div>

      <div
        className="flex items-center justify-center rounded-2xl"
        style={{ width: '100px', height: '100px', background: 'rgba(6,13,26,0.8)', border: '1px solid rgba(30,58,95,0.5)' }}
      >
        <span
          className="text-[52px] font-bold transition-colors"
          style={{ color: feedback === 'correct' ? '#10B981' : feedback === 'wrong' ? '#EF4444' : 'white' }}
        >
          {number}
        </span>
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => respond(true)}
          className="px-7 py-2.5 rounded-xl font-bold text-[13px] text-white transition-all active:scale-95"
          style={{ background: 'linear-gradient(135deg, #10B981, #059669)', boxShadow: '0 4px 12px rgba(16,185,129,0.3)' }}
        >
          YES
        </button>
        <button
          onClick={() => respond(false)}
          className="px-7 py-2.5 rounded-xl font-bold text-[13px] text-white transition-all active:scale-95"
          style={{ background: 'linear-gradient(135deg, #EF4444, #DC2626)', boxShadow: '0 4px 12px rgba(239,68,68,0.3)' }}
        >
          NO
        </button>
      </div>

      <div className="w-full max-w-[200px]">
        <div className="flex justify-between text-[10px] text-[#4A7AB5] mb-1">
          <span>Trial {Math.min(trial + 1, TOTAL)}</span>
          <span>{results.filter(r => r.correct).length}/{results.length} correct</span>
        </div>
        <div className="h-1 bg-[#1E3A5F]/40 rounded-full overflow-hidden">
          <div className="h-full bg-[#10B981] rounded-full transition-all" style={{ width: `${(trial / TOTAL) * 100}%` }} />
        </div>
      </div>
    </div>
  );
}

// ─── Pattern Shield Exercise ──────────────────────────────────────────────────

function PatternShieldExercise({ difficulty, onComplete }: { difficulty: Difficulty; onComplete: (score: number, accuracy: number, reactionMs: number) => void }) {
  const GRID = 4;
  const SEQ_LEN = difficulty === 'neuroprotect' ? 3 : difficulty === 'rehabilitate' ? 4 : 5;
  const TOTAL_ROUNDS = difficulty === 'neuroprotect' ? 5 : difficulty === 'rehabilitate' ? 7 : 9;
  const [phase, setPhase] = useState<'show' | 'recall' | 'feedback'>('show');
  const [pattern, setPattern] = useState<number[]>([]);
  const [showIdx, setShowIdx] = useState(0);
  const [userPattern, setUserPattern] = useState<number[]>([]);
  const [round, setRound] = useState(0);
  const [results, setResults] = useState<boolean[]>([]);
  const [done, setDone] = useState(false);
  const [stimTime, setStimTime] = useState(0);
  const [reactionTimes, setReactionTimes] = useState<number[]>([]);

  const startRound = useCallback(() => {
    const pat = Array.from({ length: SEQ_LEN }, () => Math.floor(Math.random() * GRID * GRID));
    setPattern(pat);
    setShowIdx(0);
    setUserPattern([]);
    setPhase('show');
  }, [SEQ_LEN]);

  useEffect(() => { startRound(); }, []);

  useEffect(() => {
    if (phase === 'show') {
      if (showIdx < pattern.length) {
        const t = setTimeout(() => setShowIdx(i => i + 1), 700);
        return () => clearTimeout(t);
      } else {
        const t = setTimeout(() => { setPhase('recall'); setStimTime(Date.now()); }, 400);
        return () => clearTimeout(t);
      }
    }
  }, [phase, showIdx, pattern]);

  const handleCellClick = (idx: number) => {
    if (phase !== 'recall') return;
    const rt = Date.now() - stimTime;
    setReactionTimes(prev => [...prev, rt]);
    const next = [...userPattern, idx];
    setUserPattern(next);
    if (next.length === pattern.length) {
      let correct = next.every((v, i) => v === pattern[i]);
      setResults(prev => [...prev, correct]);
      setPhase('feedback');
      setTimeout(() => {
        if (round + 1 >= TOTAL_ROUNDS) {
          setDone(true);
        } else {
          setRound(r => r + 1);
          startRound();
        }
      }, 700);
    }
  };

  useEffect(() => {
    if (done && results.length > 0) {
      let correct = results.filter(Boolean).length;
      const accuracy = Math.round((correct / results.length) * 100);
      const avgRt = reactionTimes.length > 0 ? Math.round(reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length) : 1000;
      const score = Math.round(accuracy * 0.8 + Math.max(0, (2000 - avgRt) / 20) * 0.2);
      onComplete(Math.min(100, score), accuracy, avgRt);
    }
  }, [done]);

  const lastResult = results[results.length - 1];

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex items-center gap-3 text-[12px]">
        <span className="text-[#8BAFD4]">
          {phase === 'show' ? `Memorise pattern (${showIdx}/${pattern.length})` : phase === 'recall' ? 'Reproduce the pattern' : lastResult ? '✓ Correct!' : '✗ Wrong'}
        </span>
        <span className="text-[#4A7AB5]">Round {round + 1}/{TOTAL_ROUNDS}</span>
      </div>

      <div
        className="grid gap-1.5"
        style={{ gridTemplateColumns: `repeat(${GRID}, 1fr)` }}
      >
        {Array.from({ length: GRID * GRID }, (_, i) => {
          const isInPattern = phase === 'show' && pattern.slice(0, showIdx).includes(i);
          const isUserSelected = userPattern.includes(i);
          const isCorrectFeedback = phase === 'feedback' && pattern.includes(i);
          return (
            <button
              key={i}
              onClick={() => handleCellClick(i)}
              className="rounded-lg transition-all duration-150 active:scale-90"
              style={{
                width: '44px',
                height: '44px',
                background: isCorrectFeedback
                  ? lastResult ? 'rgba(16,185,129,0.7)' : 'rgba(239,68,68,0.7)'
                  : isInPattern ? 'rgba(239,68,68,0.7)' : isUserSelected ?'rgba(139,92,246,0.6)' :'rgba(30,58,95,0.3)',
                border: isInPattern || isUserSelected || isCorrectFeedback
                  ? '2px solid rgba(139,92,246,0.8)'
                  : '1px solid rgba(30,58,95,0.4)',
                cursor: phase === 'recall' ? 'pointer' : 'default',
              }}
            />
          );
        })}
      </div>

      <div className="w-full max-w-[200px]">
        <div className="h-1 bg-[#1E3A5F]/40 rounded-full overflow-hidden">
          <div className="h-full bg-[#EF4444] rounded-full transition-all" style={{ width: `${(round / TOTAL_ROUNDS) * 100}%` }} />
        </div>
      </div>
    </div>
  );
}

// ─── AI Insight Panel ─────────────────────────────────────────────────────────

function AIInsightPanel({ result, exercise, difficulty, sessionHistory, onDone }: {
  result: SessionResult;
  exercise: Exercise;
  difficulty: Difficulty;
  sessionHistory: SessionResult[];
  onDone: () => void;
}) {
  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o', true);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  // Build trend context from session history (excluding current result)
  const previousSameDomain = sessionHistory.filter(
    r => r.domain === result.domain && r.exerciseId === result.exerciseId
  );
  const trendContext = previousSameDomain.length > 0
    ? `Previous ${exercise.title} scores: ${previousSameDomain.map(r => r.score).join(', ')}. ` +
      `Average previous score: ${Math.round(previousSameDomain.reduce((a, b) => a + b.score, 0) / previousSameDomain.length)}. ` +
      `Trend: ${result.score > (previousSameDomain[previousSameDomain.length - 1]?.score ?? result.score) ? 'improving' : result.score < (previousSameDomain[previousSameDomain.length - 1]?.score ?? result.score) ? 'declining' : 'stable'}.`
    : 'This is the first session for this exercise.';

  const allSessionsContext = sessionHistory.length > 0
    ? `Overall session history (${sessionHistory.length} total sessions): avg score ${Math.round(sessionHistory.reduce((a, b) => a + b.score, 0) / sessionHistory.length)}, domains trained: ${[...new Set(sessionHistory.map(r => r.domain))].join(', ')}.`
    : '';

  const scoreLabel = result.score >= 80 ? 'excellent' : result.score >= 60 ? 'good' : result.score >= 40 ? 'moderate' : 'needs improvement';

  useEffect(() => {
    sendMessage([
      {
        role: 'system',
        content: `You are a warm, expert neuropsychologist and brain training coach for the iBrain LAB platform. 
After each game session, you provide personalised, evidence-based feedback in exactly 3 clearly separated sections.

Format your response EXACTLY like this (use these exact section headers):
🎯 **Performance Tip**
[1-2 sentences: specific, actionable tip based on their score and the cognitive domain trained. Reference neuroscience where relevant.]

💪 **Encouragement**
[1-2 sentences: warm, genuine encouragement tailored to their performance level and trend. Be specific, not generic.]

📈 **Trend Insight**
[1-2 sentences: insight about their progress trend across sessions. If first session, give a motivating baseline message.]

Keep each section concise. Be warm, clinical, and specific. Never be generic or use filler phrases.`,
      },
      {
        role: 'user',
        content: `Game Session Results:
Exercise: ${exercise.title} (${DOMAIN_LABELS[exercise.domain]} domain)
Difficulty: ${DIFFICULTY_CONFIG[difficulty].label}
Score: ${result.score}/100 (${scoreLabel})
Accuracy: ${result.accuracy}%
Average reaction time: ${result.reactionMs}ms
XP earned: ${result.xpEarned}

${trendContext}
${allSessionsContext}

Generate personalised performance tips, encouragement, and trend insights for this player.`,
      },
    ], { max_completion_tokens: 300 });
  }, []);

  // Parse sections from response
  const sections = React.useMemo(() => {
    if (!response) return null;
    const tipMatch = response.match(/🎯\s*\*\*Performance Tip\*\*\s*([\s\S]*?)(?=💪|$)/);
    const encourageMatch = response.match(/💪\s*\*\*Encouragement\*\*\s*([\s\S]*?)(?=📈|$)/);
    const trendMatch = response.match(/📈\s*\*\*Trend Insight\*\*\s*([\s\S]*?)$/);
    return {
      tip: tipMatch?.[1]?.trim() || '',
      encourage: encourageMatch?.[1]?.trim() || '',
      trend: trendMatch?.[1]?.trim() || '',
      raw: !tipMatch && !encourageMatch && !trendMatch ? response : '',
    };
  }, [response]);

  const scoreColor = result.score >= 80 ? '#10B981' : result.score >= 60 ? '#F97316' : result.score >= 40 ? '#06B6D4' : '#EF4444';
  const trendIcon = previousSameDomain.length > 0
    ? result.score > (previousSameDomain[previousSameDomain.length - 1]?.score ?? result.score) ? '↑' : result.score < (previousSameDomain[previousSameDomain.length - 1]?.score ?? result.score) ? '↓' : '→'
    : '★';

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles size={16} style={{ color: '#8B5CF6' }} />
          <span className="text-[14px] font-bold text-white">AI Performance Coach</span>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
          style={{ background: `${scoreColor}18`, color: scoreColor, border: `1px solid ${scoreColor}30` }}>
          {trendIcon} {result.score}/100
        </div>
      </div>

      {/* Score context bar */}
      <div className="rounded-xl p-3.5"
        style={{ background: 'rgba(30,58,95,0.25)', border: '1px solid rgba(30,58,95,0.4)' }}>
        <div className="flex items-center gap-1.5 mb-2">
          <Target size={13} style={{ color: '#F97316' }} />
          <span className="text-[11px] font-bold text-[#F97316] uppercase tracking-wide">Performance Tip</span>
        </div>
        <p className="text-[12px] sm:text-[13px] text-zinc-300 leading-relaxed">{sections.tip}</p>
      </div>

      {/* Encouragement */}
      <div className="rounded-xl p-3.5"
        style={{ background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.2)' }}>
        <div className="flex items-center gap-1.5 mb-2">
          <Award size={13} style={{ color: '#10B981' }} />
          <span className="text-[11px] font-bold text-[#10B981] uppercase tracking-wide">Encouragement</span>
        </div>
        <p className="text-[12px] sm:text-[13px] text-zinc-300 leading-relaxed">{sections.encourage}</p>
      </div>

      {/* Trend Insight */}
      <div className="rounded-xl p-3.5"
        style={{ background: 'rgba(139,92,246,0.07)', border: '1px solid rgba(139,92,246,0.2)' }}>
        <div className="flex items-center gap-1.5 mb-2">
          <TrendingUp size={13} style={{ color: '#8B5CF6' }} />
          <span className="text-[11px] font-bold text-[#8B5CF6] uppercase tracking-wide">Trend Insight</span>
        </div>
        <p className="text-[12px] sm:text-[13px] text-zinc-300 leading-relaxed">{sections.trend}</p>
      </div>

      <button
        onClick={onDone}
        disabled={isLoading}
        className="w-full py-3 rounded-xl font-bold text-[13px] text-white transition-all active:scale-[0.98]"
        style={{
          background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
          boxShadow: '0 4px 16px rgba(139,92,246,0.3)',
          opacity: isLoading ? 0.6 : 1,
        }}
      >
        Continue Training →
      </button>
    </div>
  );
}
// ─── Biomarker Recommendation Types ──────────────────────────────────────────

interface ExerciseRecommendation {
  exerciseId: string;
  priority: 'high' | 'medium' | 'low';
  reason: string;
}

interface BiomarkerRecommendationResult {
  recommendedDifficulty: Difficulty;
  exercises: ExerciseRecommendation[];
  clinicalRationale: string;
  urgencyNote?: string;
}

// ─── Biomarker Recommendation Panel ──────────────────────────────────────────

function BiomarkerRecommendationPanel({
  onApplyRecommendation,
}: {
  onApplyRecommendation: (difficulty: Difficulty, exerciseId: string) => void;
}) {
  const { metrics } = useSession();
  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-5', false);
  const [parsed, setParsed] = useState<BiomarkerRecommendationResult | null>(null);
  const [parseError, setParseError] = useState(false);
  const [lastMetricsSnapshot, setLastMetricsSnapshot] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    if (error) toast.error('NeuroTrainer AI: ' + error.message);
  }, [error]);

  const fetchRecommendation = useCallback(() => {
    const {
      thetaAlphaRatio,
      interHemisphericCoherence,
      thetaPower,
      alphaPower,
      betaPower,
      deltaPower,
      gammaPower,
      attentionScore,
      cognitiveLoadIndex,
    } = metrics;

    const snapshot = `${thetaAlphaRatio.toFixed(2)}-${interHemisphericCoherence.toFixed(0)}-${thetaPower.toFixed(1)}-${betaPower.toFixed(1)}`;
    if (snapshot === lastMetricsSnapshot && parsed) return;
    setLastMetricsSnapshot(snapshot);
    setParsed(null);
    setParseError(false);

    // Derive severity labels from raw metrics
    const alzheimerSeverity =
      thetaAlphaRatio >= 2.0 ? 'High' :
      thetaAlphaRatio >= 1.6 ? 'Elevated' :
      thetaAlphaRatio >= 1.2 ? 'Moderate' : 'Low';

    const tbr = betaPower > 0 ? thetaPower / betaPower : 1.4;
    const adhdSeverity =
      tbr >= 2.0 ? 'High' :
      tbr >= 1.6 ? 'Elevated' :
      tbr >= 1.2 ? 'Borderline' : 'Low';

    const coherenceStatus =
      interHemisphericCoherence < 55 ? 'Severely Reduced' :
      interHemisphericCoherence < 65 ? 'Reduced' :
      interHemisphericCoherence < 75 ? 'Mildly Reduced' : 'Normal';

    sendMessage(
      [
        {
          role: 'system',
          content: `You are a clinical neuropsychologist and EEG specialist designing personalised brain rehabilitation programs.
You will receive live EEG biomarker data and must recommend specific NeuroTrainer exercises and difficulty level.

Available exercises (use exact IDs):
- n-back: Working memory, hippocampal plasticity (Memory domain)
- attention-spotlight: Selective attention, prefrontal-parietal networks (Attention domain)
- processing-speed: Neural conduction velocity, white matter integrity (Processing domain)
- executive-switch: Cognitive flexibility, dorsolateral prefrontal cortex (Executive domain)
- pattern-shield: Visuospatial memory, posterior cortical networks (Memory domain)

Available difficulty levels: neuroprotect | rehabilitate | enhance

Respond ONLY with a valid JSON object matching this exact schema (no markdown, no extra text):
{
  "recommendedDifficulty": "neuroprotect" | "rehabilitate" | "enhance",
  "exercises": [
    { "exerciseId": "<id>", "priority": "high" | "medium" | "low", "reason": "<1 sentence clinical reason>" },
    ...
  ],
  "clinicalRationale": "<2-3 sentence summary of why these exercises address the biomarker profile>",
  "urgencyNote": "<optional 1 sentence if biomarkers indicate urgent concern, omit if not needed>"
}

Rules:
- Recommend 2-4 exercises ordered by priority
- For Alzheimer biomarkers: elevated theta/alpha ratio → prioritise memory and executive exercises; reduced coherence → prioritise attention and processing
- For ADHD biomarkers: elevated TBR → prioritise attention-spotlight and executive-switch; use neuroprotect if severe
- High severity → neuroprotect; moderate → rehabilitate; low → enhance
- Always ground recommendations in the specific biomarker values provided`,
        },
        {
          role: 'user',
          content: `Live EEG Biomarker Profile:

ALZHEIMER BIOMARKERS:
- Theta/Alpha Slowing Index: ${thetaAlphaRatio.toFixed(2)} (normal: 0.8–1.4; elevated ≥1.8 = significant slowing) → Severity: ${alzheimerSeverity}
- Inter-Hemispheric Coherence: ${interHemisphericCoherence.toFixed(0)}% (normal ≥70%; reduced = connectivity loss) → Status: ${coherenceStatus}
- Theta Power: ${thetaPower.toFixed(1)} µV² | Alpha Power: ${alphaPower.toFixed(1)} µV² | Delta Power: ${deltaPower.toFixed(1)} µV²

ADHD BIOMARKERS:
- Theta/Beta Ratio (TBR): ${tbr.toFixed(2)} (normal <1.2; elevated ≥1.6 = attention dysregulation) → Severity: ${adhdSeverity}
- Theta Power: ${thetaPower.toFixed(1)} µV² | Beta Power: ${betaPower.toFixed(1)} µV²

ADDITIONAL METRICS:
- Gamma Power: ${gammaPower.toFixed(1)} µV² | Cognitive Load Index: ${cognitiveLoadIndex.toFixed(0)}/100 | Attention Score: ${attentionScore.toFixed(0)}/100

Recommend the optimal NeuroTrainer exercises and difficulty for this biomarker profile.`,
        },
      ],
      { max_completion_tokens: 600 }
    );
  }, [metrics, sendMessage, lastMetricsSnapshot, parsed]);

  // Auto-fetch on mount
  useEffect(() => {
    fetchRecommendation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Parse JSON response
  useEffect(() => {
    if (!response || isLoading) return;
    try {
      const cleaned = response.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const result: BiomarkerRecommendationResult = JSON.parse(cleaned);
      setParsed(result);
      setParseError(false);
    } catch {
      setParseError(true);
    }
  }, [response, isLoading]);

  const priorityColor = (p: ExerciseRecommendation['priority']) =>
    p === 'high' ? '#EF4444' : p === 'medium' ? '#F97316' : '#10B981';

  const priorityLabel = (p: ExerciseRecommendation['priority']) =>
    p === 'high' ? 'HIGH' : p === 'medium' ? 'MED' : 'LOW';

  const exerciseLabel = (id: string) =>
    EXERCISES.find(e => e.id === id)?.title ?? id;

  const exerciseColor = (id: string) =>
    EXERCISES.find(e => e.id === id)?.color ?? '#8B5CF6';

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(139,92,246,0.06) 0%, rgba(6,13,26,0.95) 100%)',
        border: '1px solid rgba(139,92,246,0.3)',
        boxShadow: '0 0 24px rgba(139,92,246,0.08)',
      }}
    >
      {/* Panel Header */}
      <div
        onClick={() => setIsExpanded(e => !e)}
        className="w-full px-4 py-3 flex items-center justify-between transition-all cursor-pointer"
        style={{ borderBottom: isExpanded ? '1px solid rgba(139,92,246,0.15)' : 'none' }}
      >
        <div className="flex items-center gap-2.5">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ background: 'rgba(139,92,246,0.18)', border: '1px solid rgba(139,92,246,0.35)' }}
          >
            <Sparkles size={14} style={{ color: '#8B5CF6' }} />
          </div>
          <div className="text-left">
            <div className="text-[12px] font-bold text-white leading-tight">AI Biomarker Recommendations</div>
            <div className="text-[10px] text-[#4A7AB5]">Personalised from live EEG · Alzheimer/ADHD profile</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isLoading && (
            <div className="flex gap-1">
              {[0, 1, 2].map(i => (
                <div
                  key={i}
                  className="w-1 h-1 rounded-full bg-[#8B5CF6] animate-bounce"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
          )}
          {!isLoading && (
            <button
              onClick={() => { e.stopPropagation(); fetchRecommendation(); }}
              className="p-1 rounded-lg transition-all hover:bg-[#1E3A5F]/40 active:scale-90"
              title="Refresh recommendations"
            >
              <RefreshCw size={12} style={{ color: '#4A7AB5' }} />
            </button>
          )}
          <ChevronRight
            size={14}
            className="text-[#4A7AB5] transition-transform duration-200"
            style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
          />
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 pt-3 space-y-3">
          {/* Loading state */}
          {isLoading && !parsed && (
            <div className="flex items-center gap-3 py-3">
              <div className="flex gap-1.5">
                {[0, 1, 2, 3].map(i => (
                  <div
                    key={i}
                    className="w-1.5 h-1.5 rounded-full bg-[#8B5CF6] animate-bounce"
                    style={{ animationDelay: `${i * 0.12}s` }}
                  />
                ))}
              </div>
              <span className="text-[12px] text-[#4A7AB5]">Analysing Alzheimer/ADHD biomarker profile…</span>
            </div>
          )}

          {/* Parse error */}
          {parseError && !isLoading && (
            <div className="flex items-center gap-2 py-2">
              <AlertCircle size={13} style={{ color: '#F97316' }} />
              <span className="text-[11px] text-[#F97316]">Could not parse recommendation. Try refreshing.</span>
            </div>
          )}

          {/* Recommendation content */}
          {parsed && !isLoading && (
            <>
              {/* Urgency note */}
              {parsed.urgencyNote && (
                <div
                  className="flex items-start gap-2 px-3 py-2 rounded-xl"
                  style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}
                >
                  <AlertCircle size={12} className="shrink-0 mt-0.5" style={{ color: '#EF4444' }} />
                  <p className="text-[11px] leading-relaxed" style={{ color: '#FCA5A5' }}>{parsed.urgencyNote}</p>
                </div>
              )}

              {/* Recommended difficulty */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-[#4A7AB5] uppercase tracking-wider font-semibold">Recommended Mode</span>
                <div
                  className="px-2.5 py-0.5 rounded-full text-[10px] font-bold"
                  style={{
                    background: `${DIFFICULTY_CONFIG[parsed.recommendedDifficulty].color}18`,
                    border: `1px solid ${DIFFICULTY_CONFIG[parsed.recommendedDifficulty].color}40`,
                    color: DIFFICULTY_CONFIG[parsed.recommendedDifficulty].color,
                  }}
                >
                  {DIFFICULTY_CONFIG[parsed.recommendedDifficulty].label}
                </div>
              </div>

              {/* Exercise recommendations */}
              <div className="space-y-2">
                {parsed.exercises.map((rec, idx) => {
                  const ex = EXERCISES.find(e => e.id === rec.exerciseId);
                  if (!ex) return null;
                  return (
                    <div
                      key={rec.exerciseId}
                      className="rounded-xl p-3 flex items-start gap-3"
                      style={{
                        background: idx === 0 ? `${exerciseColor(rec.exerciseId)}08` : 'rgba(30,58,95,0.15)',
                        border: `1px solid ${idx === 0 ? exerciseColor(rec.exerciseId) + '30' : 'rgba(30,58,95,0.35)'}`,
                      }}
                    >
                      {/* Priority badge */}
                      <div
                        className="shrink-0 px-1.5 py-0.5 rounded text-[8px] font-black tracking-wider mt-0.5"
                        style={{
                          background: `${priorityColor(rec.priority)}18`,
                          border: `1px solid ${priorityColor(rec.priority)}35`,
                          color: priorityColor(rec.priority),
                        }}
                      >
                        {priorityLabel(rec.priority)}
                      </div>

                      {/* Exercise info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <span
                            className="text-[12px] font-bold"
                            style={{ color: exerciseColor(rec.exerciseId) }}
                          >
                            {exerciseLabel(rec.exerciseId)}
                          </span>
                          <span className="text-[9px] text-[#4A7AB5] shrink-0">{DOMAIN_LABELS[ex.domain]}</span>
                        </div>
                        <p className="text-[10px] text-[#8BAFD4] leading-relaxed">{rec.reason}</p>
                      </div>

                      {/* Quick start button */}
                      <button
                        onClick={() => onApplyRecommendation(parsed.recommendedDifficulty, rec.exerciseId)}
                        className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-all active:scale-90"
                        style={{
                          background: `${exerciseColor(rec.exerciseId)}18`,
                          border: `1px solid ${exerciseColor(rec.exerciseId)}35`,
                        }}
                        title={`Start ${exerciseLabel(rec.exerciseId)}`}
                      >
                        <Play size={10} style={{ color: exerciseColor(rec.exerciseId) }} />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Clinical rationale */}
              <div
                className="rounded-xl px-3 py-2.5"
                style={{ background: 'rgba(30,58,95,0.2)', border: '1px solid rgba(30,58,95,0.35)' }}
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Brain size={10} style={{ color: '#4A7AB5' }} />
                  <span className="text-[9px] text-[#4A7AB5] uppercase tracking-wider font-semibold">Clinical Rationale</span>
                </div>
                <p className="text-[11px] text-[#8BAFD4] leading-relaxed">{parsed.clinicalRationale}</p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main NeuroTrainer Component ──────────────────────────────────────────────

export default function NeuroTrainer() {
  const [difficulty, setDifficulty] = useState<Difficulty>('rehabilitate');
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);
  const [phase, setPhase] = useState<ExercisePhase>('idle');
  const [sessionResults, setSessionResults] = useState<SessionResult[]>([]);
  const [currentResult, setCurrentResult] = useState<SessionResult | null>(null);
  const [totalXP, setTotalXP] = useState(0);
  const [domainProgress, setDomainProgress] = useState<DomainProgress[]>([
    { domain: 'attention', score: 0, sessions: 0, trend: 0 },
    { domain: 'memory', score: 0, sessions: 0, trend: 0 },
    { domain: 'processing', score: 0, sessions: 0, trend: 0 },
    { domain: 'executive', score: 0, sessions: 0, trend: 0 },
  ]);
  const [neuroplasticityScore, setNeuroplasticityScore] = useState(0);
  const [timer, setTimer] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load persisted XP
  useEffect(() => {
    const saved = localStorage.getItem('neurotrainer_xp');
    if (saved) setTotalXP(parseInt(saved, 10));
    const savedProgress = localStorage.getItem('neurotrainer_progress');
    if (savedProgress) {
      try { setDomainProgress(JSON.parse(savedProgress)); } catch {}
    }
    const savedNP = localStorage.getItem('neurotrainer_np');
    if (savedNP) setNeuroplasticityScore(parseInt(savedNP, 10));
  }, []);

  const startExercise = (exercise: Exercise) => {
    setSelectedExercise(exercise);
    setPhase('briefing');
    setTimer(exercise.durationSec);
  };

  // Apply AI recommendation: set difficulty and immediately start the exercise
  const applyRecommendation = useCallback((recommendedDifficulty: Difficulty, exerciseId: string) => {
    const exercise = EXERCISES.find(e => e.id === exerciseId);
    if (!exercise) return;
    setDifficulty(recommendedDifficulty);
    setSelectedExercise(exercise);
    setPhase('briefing');
    setTimer(exercise.durationSec);
  }, []);

  const beginActive = () => {
    setPhase('active');
    timerRef.current = setInterval(() => setTimer(t => Math.max(0, t - 1)), 1000);
  };

  const handleExerciseComplete = (score: number, accuracy: number, reactionMs: number) => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!selectedExercise) return;

    const xpMultiplier = difficulty === 'neuroprotect' ? 0.8 : difficulty === 'rehabilitate' ? 1.0 : 1.3;
    const xpEarned = Math.round(selectedExercise.xpReward * (score / 100) * xpMultiplier);

    const result: SessionResult = {
      exerciseId: selectedExercise.id,
      score,
      accuracy,
      reactionMs,
      xpEarned,
      domain: selectedExercise.domain,
    };

    setCurrentResult(result);
    setSessionResults(prev => [...prev, result]);

    // Update XP
    const newXP = totalXP + xpEarned;
    setTotalXP(newXP);
    localStorage.setItem('neurotrainer_xp', newXP.toString());

    // Update domain progress
    setDomainProgress(prev => {
      const updated = prev.map(dp => {
        if (dp.domain !== selectedExercise.domain) return dp;
        const newScore = dp.sessions === 0 ? score : Math.round((dp.score * dp.sessions + score) / (dp.sessions + 1));
        const trend = newScore - dp.score;
        return { ...dp, score: newScore, sessions: dp.sessions + 1, trend };
      });
      localStorage.setItem('neurotrainer_progress', JSON.stringify(updated));
      return updated;
    });

    // Update neuroplasticity score
    const allScores = [...sessionResults, result].map(r => r.score);
    const avgScore = Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length);
    const np = Math.min(100, Math.round(avgScore * 0.7 + allScores.length * 2));
    setNeuroplasticityScore(np);
    localStorage.setItem('neurotrainer_np', np.toString());

    setPhase('result');
  };

  const goToAIInsight = () => setPhase('ai_insight');

  const resetToIdle = () => {
    setPhase('idle');
    setSelectedExercise(null);
    setCurrentResult(null);
    setTimer(0);
  };

  const domainColors: Record<Domain, string> = {
    attention: '#F97316',
    memory: '#8B5CF6',
    processing: '#06B6D4',
    executive: '#10B981',
    language: '#F59E0B',
  };

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, rgba(11,22,40,0.95) 0%, rgba(6,13,26,0.98) 100%)',
        border: '1px solid rgba(139,92,246,0.25)',
        boxShadow: '0 0 40px rgba(139,92,246,0.06)',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-3 flex items-center justify-between gap-2 flex-wrap"
        style={{ borderBottom: '1px solid rgba(30,58,95,0.5)', background: 'rgba(139,92,246,0.05)' }}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)' }}
          >
            <Dumbbell size={18} style={{ color: '#8B5CF6' }} />
          </div>
          <div className="min-w-0">
            <h3 className="text-[14px] sm:text-[15px] font-bold text-white">NeuroTrainer</h3>
            <p className="text-[10px] sm:text-[11px] text-[#4A7AB5] truncate">AI-Powered Brain Rehabilitation</p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <div className="text-right">
            <div className="text-[10px] text-[#4A7AB5]">XP</div>
            <div className="text-[13px] sm:text-[15px] font-bold text-[#8B5CF6]">{totalXP.toLocaleString()}</div>
          </div>
          <div
            className="w-px h-6"
            style={{ background: 'rgba(30,58,95,0.6)' }}
          />
          <div className="text-right">
            <div className="text-[10px] text-[#4A7AB5]">Neuroplasticity</div>
            <div className="text-[13px] sm:text-[15px] font-bold" style={{ color: neuroplasticityScore > 60 ? '#10B981' : neuroplasticityScore > 30 ? '#F97316' : '#8BAFD4' }}>
              {neuroplasticityScore}%
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-5">
        {/* ── IDLE: Exercise Selection ── */}
        {phase === 'idle' && (
          <div className="space-y-5">
            {/* AI Biomarker Recommendations */}
            <BiomarkerRecommendationPanel onApplyRecommendation={applyRecommendation} />

            {/* Difficulty selector */}
            <div>
              <p className="text-[11px] text-[#4A7AB5] uppercase tracking-wider mb-3 font-semibold">Training Mode</p>
              <div className="grid grid-cols-3 gap-2">
                {(Object.keys(DIFFICULTY_CONFIG) as Difficulty[]).map(d => (
                  <button
                    key={d}
                    onClick={() => setDifficulty(d)}
                    className="rounded-xl p-3 text-left transition-all"
                    style={{
                      background: difficulty === d ? `rgba(${d === 'neuroprotect' ? '16,185,129' : d === 'rehabilitate' ? '249,115,22' : '139,92,246'},0.15)` : 'rgba(30,58,95,0.2)',
                      border: `1px solid ${difficulty === d ? DIFFICULTY_CONFIG[d].color + '50' : 'rgba(30,58,95,0.4)'}`,
                    }}
                  >
                    <div className="text-[12px] font-bold mb-1" style={{ color: DIFFICULTY_CONFIG[d].color }}>
                      {DIFFICULTY_CONFIG[d].label}
                    </div>
                    <div className="text-[10px] text-[#4A7AB5] leading-tight">{DIFFICULTY_CONFIG[d].description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Domain Progress */}
            <div>
              <p className="text-[11px] text-[#4A7AB5] uppercase tracking-wider mb-3 font-semibold">Domain Progress</p>
              <div className="grid grid-cols-2 gap-2">
                {domainProgress.map(dp => (
                  <div
                    key={dp.domain}
                    className="rounded-xl p-3"
                    style={{ background: 'rgba(30,58,95,0.2)', border: '1px solid rgba(30,58,95,0.4)' }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] text-[#8BAFD4] font-medium">{DOMAIN_LABELS[dp.domain]}</span>
                      <div className="flex items-center gap-1">
                        {dp.trend > 0 && <TrendingUp size={10} style={{ color: '#10B981' }} />}
                        <span className="text-[11px] font-bold" style={{ color: domainColors[dp.domain] }}>{dp.score}</span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-[#1E3A5F]/40 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${dp.score}%`, background: domainColors[dp.domain] }}
                      />
                    </div>
                    <div className="text-[10px] text-[#4A7AB5] mt-1">{dp.sessions} session{dp.sessions !== 1 ? 's' : ''}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Exercise Cards */}
            <div>
              <p className="text-[11px] text-[#4A7AB5] uppercase tracking-wider mb-3 font-semibold">Choose Exercise</p>
              <div className="space-y-2">
                {EXERCISES.map(ex => (
                  <button
                    key={ex.id}
                    onClick={() => startExercise(ex)}
                    className="w-full rounded-xl p-4 text-left transition-all hover:scale-[1.01] active:scale-[0.99] group"
                    style={{
                      background: 'rgba(30,58,95,0.2)',
                      border: `1px solid rgba(30,58,95,0.4)`,
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = ex.color + '50'; (e.currentTarget as HTMLElement).style.background = `rgba(${ex.color === '#8B5CF6' ? '139,92,246' : ex.color === '#F97316' ? '249,115,22' : ex.color === '#06B6D4' ? '6,182,212' : ex.color === '#10B981' ? '16,185,129' : '239,68,68'},0.08)`; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(30,58,95,0.4)'; (e.currentTarget as HTMLElement).style.background = 'rgba(30,58,95,0.2)'; }}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                        style={{ background: `${ex.color}18`, border: `1px solid ${ex.color}30`, color: ex.color }}
                      >
                        {ex.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[13px] font-bold text-white">{ex.title}</span>
                          <div className="flex items-center gap-2 shrink-0 ml-2">
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: `${ex.color}18`, color: ex.color }}>
                              {DOMAIN_LABELS[ex.domain]}
                            </span>
                            <span className="text-[10px] text-[#4A7AB5] flex items-center gap-1">
                              <Clock size={9} />
                              {ex.durationSec}s
                            </span>
                          </div>
                        </div>
                        <p className="text-[11px] text-[#4A7AB5] leading-relaxed line-clamp-2">{ex.description}</p>
                        <div className="flex items-center gap-1 mt-2">
                          <Star size={10} style={{ color: '#F59E0B' }} />
                          <span className="text-[10px] text-[#F59E0B]">+{ex.xpReward} XP</span>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-[#4A7AB5] shrink-0 mt-2 group-hover:text-white transition-colors" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Session summary */}
            {sessionResults.length > 0 && (
              <div
                className="rounded-xl p-4"
                style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Award size={14} style={{ color: '#8B5CF6' }} />
                  <span className="text-[12px] font-bold text-white">Session Summary</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center">
                    <div className="text-[18px] font-bold text-[#8B5CF6]">{sessionResults.length}</div>
                    <div className="text-[10px] text-[#4A7AB5]">Exercises</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[18px] font-bold text-[#10B981]">
                      {Math.round(sessionResults.reduce((a, b) => a + b.score, 0) / sessionResults.length)}
                    </div>
                    <div className="text-[10px] text-[#4A7AB5]">Avg Score</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[18px] font-bold text-[#F59E0B]">
                      {sessionResults.reduce((a, b) => a + b.xpEarned, 0)}
                    </div>
                    <div className="text-[10px] text-[#4A7AB5]">XP Earned</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── BRIEFING ── */}
        {phase === 'briefing' && selectedExercise && (
          <div className="flex flex-col items-center gap-5 py-2">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{
                background: `${selectedExercise.color}18`,
                border: `1px solid ${selectedExercise.color}30`,
                color: selectedExercise.color,
                boxShadow: `0 0 24px ${selectedExercise.color}18`,
              }}
            >
              {selectedExercise.icon}
            </div>
            <div className="text-center">
              <h4 className="text-[18px] font-bold text-white mb-2">{selectedExercise.title}</h4>
              <p className="text-[13px] text-[#8BAFD4] leading-relaxed max-w-[380px]">{selectedExercise.description}</p>
            </div>
            <div className="flex items-center gap-4 text-[12px]">
              <div className="flex items-center gap-1.5 text-[#4A7AB5]">
                <Clock size={13} />
                <span>~{selectedExercise.durationSec}s</span>
              </div>
              <div className="flex items-center gap-1.5" style={{ color: DIFFICULTY_CONFIG[difficulty].color }}>
                <Shield size={13} />
                <span>{DIFFICULTY_CONFIG[difficulty].label}</span>
              </div>
              <div className="flex items-center gap-1.5 text-[#F59E0B]">
                <Star size={13} />
                <span>Up to {selectedExercise.xpReward} XP</span>
              </div>
            </div>
            <div className="flex gap-3 w-full max-w-[320px]">
              <button
                onClick={resetToIdle}
                className="flex-1 py-2.5 rounded-xl text-[13px] font-medium text-[#8BAFD4] border border-[#1E3A5F]/60 hover:bg-[#0F1E35] transition-all"
              >
                ← Back
              </button>
              <button
                onClick={beginActive}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-[13px] font-bold text-white transition-all active:scale-[0.98]"
                style={{
                  background: `linear-gradient(135deg, ${selectedExercise.color}, ${selectedExercise.color}CC)`,
                  boxShadow: `0 4px 16px ${selectedExercise.color}35`,
                }}
              >
                <Play size={14} />
                Begin
              </button>
            </div>
          </div>
        )}

        {/* ── ACTIVE ── */}
        {phase === 'active' && selectedExercise && (
          <div className="flex flex-col gap-4">
            {/* Timer bar */}
            <div className="flex items-center justify-between mb-1">
              <span className="text-[12px] font-bold" style={{ color: selectedExercise.color }}>{selectedExercise.title}</span>
              <div className="flex items-center gap-1.5 text-[#8BAFD4] text-[12px]">
                <Clock size={12} />
                <span className="tabular-nums">{timer}s</span>
              </div>
            </div>
            <div className="h-1 bg-[#1E3A5F]/40 rounded-full overflow-hidden mb-2">
              <div
                className="h-full rounded-full transition-all duration-1000"
                style={{
                  width: `${(timer / selectedExercise.durationSec) * 100}%`,
                  background: timer < 10 ? '#EF4444' : selectedExercise.color,
                }}
              />
            </div>

            {selectedExercise.id === 'n-back' && (
              <NBackExercise difficulty={difficulty} onComplete={handleExerciseComplete} />
            )}
            {selectedExercise.id === 'attention-spotlight' && (
              <AttentionSpotlightExercise difficulty={difficulty} onComplete={handleExerciseComplete} />
            )}
            {selectedExercise.id === 'processing-speed' && (
              <SynapseSprintExercise difficulty={difficulty} onComplete={handleExerciseComplete} />
            )}
            {selectedExercise.id === 'executive-switch' && (
              <CortexSwitchExercise difficulty={difficulty} onComplete={handleExerciseComplete} />
            )}
            {selectedExercise.id === 'pattern-shield' && (
              <PatternShieldExercise difficulty={difficulty} onComplete={handleExerciseComplete} />
            )}
          </div>
        )}

        {/* ── RESULT ── */}
        {phase === 'result' && currentResult && selectedExercise && (
          <div className="flex flex-col items-center gap-5 py-2">
            {/* Score ring */}
            <div className="relative">
              <svg width="120" height="120" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(30,58,95,0.4)" strokeWidth="8" />
                <circle
                  cx="60" cy="60" r="50"
                  fill="none"
                  stroke={currentResult.score >= 70 ? '#10B981' : currentResult.score >= 40 ? '#F97316' : '#EF4444'}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${(currentResult.score / 100) * 314} 314`}
                  transform="rotate(-90 60 60)"
                  style={{ transition: 'stroke-dasharray 1s ease' }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-[28px] font-bold text-white">{currentResult.score}</span>
                <span className="text-[10px] text-[#4A7AB5]">Score</span>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 w-full">
              {[
                { label: 'Accuracy', value: `${currentResult.accuracy}%`, color: '#10B981' },
                { label: 'Reaction', value: `${currentResult.reactionMs}ms`, color: '#06B6D4' },
                { label: 'XP Earned', value: `+${currentResult.xpEarned}`, color: '#F59E0B' },
              ].map(stat => (
                <div
                  key={stat.label}
                  className="rounded-xl p-3 text-center"
                  style={{ background: 'rgba(30,58,95,0.2)', border: '1px solid rgba(30,58,95,0.4)' }}
                >
                  <div className="text-[16px] font-bold" style={{ color: stat.color }}>{stat.value}</div>
                  <div className="text-[10px] text-[#4A7AB5]">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 w-full">
              <button
                onClick={resetToIdle}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[13px] font-medium text-[#8BAFD4] border border-[#1E3A5F]/60 hover:bg-[#0F1E35] transition-all"
              >
                <RotateCcw size={13} />
                Menu
              </button>
              <button
                onClick={goToAIInsight}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-[13px] font-bold text-white transition-all active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)', boxShadow: '0 4px 16px rgba(139,92,246,0.3)' }}
              >
                <Sparkles size={13} />
                Get AI Tips
              </button>
            </div>
          </div>
        )}

        {/* ── AI INSIGHT ── */}
        {phase === 'ai_insight' && currentResult && selectedExercise && (
          <AIInsightPanel
            result={currentResult}
            exercise={selectedExercise}
            difficulty={difficulty}
            sessionHistory={sessionResults.filter(r => r !== currentResult)}
            onDone={resetToIdle}
          />
        )}
      </div>
    </div>
  );
}
